<template>
  <nav
    class="relative z-10 flex items-center justify-between h-16 px-3 py-3 mb-4 bg-gray-700 print:hidden"
  >
    <div class="flex-1 hidden md:block">
      <logo></logo>
    </div>

    <div class="flex justify-center flex-1 space-x-3 md:space-x-5 text-center">
      <span class="relative group">
        <button
          class="duration-150 transform border border-transparent rounded-lg group hover:-translate-y-px hover:shadow-xs focus:outline-none"
          @click="showColors = !showColors"
          v-click-outside="closeColorsMenu"
        >
          <svg
            class="relative flex-shrink-0 w-6 h-6 m-2 text-gray-300 group-hover:text-gray-200"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
            />
          </svg>
        </button>
        <transition
          enter-active-class="transition duration-200 ease-out transform"
          enter-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-200 ease-in transform"
          leave-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div
            v-show="showColors"
            class="absolute left-0 grid w-48 h-32 grid-flow-row grid-cols-3 grid-rows-3 px-3 py-2 text-gray-600 bg-gray-600 rounded-lg shadow-lg md:-ml-20"
          >
            <div
              class="flex items-center justify-center"
              v-for="(color, index) in colors"
              :key="index"
            >
              <button
                class="block w-1/2 p-3 duration-150 transform border-2 border-transparent rounded-lg hover:-translate-y-px hover:shadow-xs"
                @click="
                  $emit('changeColor', color.color);
                  showColors = false;
                "
                :class="[color.value]"
              ></button>
            </div>
          </div>
        </transition>
      </span>

      <div class="relative inline-block">
        <button
          class="duration-150 transform border border-transparent rounded-lg group hover:-translate-y-px hover:shadow-xs focus:outline-none"
          @click="showFonts = !showFonts"
          v-click-outside="closeFontsMenu"
        >
          <svg
            class="relative flex-shrink-0 w-6 h-6 m-2 text-gray-300 group-hover:text-gray-200"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            viewBox="0 0 448 512"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="40"
              d="M432 416h-23.41L277.88 53.69A32 32 0 0 0 247.58 32h-47.16a32 32 0 0 0-30.3 21.69L39.41 416H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16h-19.58l23.3-64h152.56l23.3 64H304a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM176.85 272L224 142.51 271.15 272z"
            />
          </svg>
        </button>
        <transition
          enter-active-class="transition duration-200 ease-out transform"
          enter-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-200 ease-in transform"
          leave-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div
            class="absolute left-0 py-1 -ml-12 text-left bg-gray-600 rounded-lg shadow-xs w-36"
            v-show="showFonts"
          >
            <span
              class="grid w-full grid-cols-4 px-2 py-2 text-sm font-medium text-left text-gray-100 cursor-pointer hover:bg-gray-500 focus:outline-none hover:text-gray-50 font-ubuntu"
              @click="$emit('changeFont', 'ubuntu')"
            >
              <span class="relative col-span-1 pr-2">
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  v-show="globalStyles.font === 'font-ubuntu'"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span class="col-span-3">{{ $t("toolbar.fonts.a") }}</span>
            </span>
            <span
              class="grid w-full grid-cols-4 px-2 py-2 font-sans text-sm font-medium text-left text-gray-100 cursor-pointer hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="$emit('changeFont', 'sansprint')"
            >
              <span class="relative col-span-1 pr-2">
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  v-show="
                    globalStyles.font === 'font-sansprint' ||
                    globalStyles.font === 'font-sans'
                  "
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span class="col-span-3">{{ $t("toolbar.fonts.b") }}</span>
            </span>
            <span
              class="grid w-full grid-cols-4 px-2 py-2 text-sm font-medium text-left text-gray-100 cursor-pointer hover:bg-gray-500 focus:outline-none hover:text-gray-50 font-nunito"
              @click="$emit('changeFont', 'nunito')"
            >
              <span class="relative col-span-1 pr-2">
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  v-show="globalStyles.font === 'font-nunito'"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span class="col-span-3">{{ $t("toolbar.fonts.c") }}</span>
            </span>
            <span
              class="grid w-full grid-cols-4 px-2 py-2 text-sm font-medium text-left text-gray-100 cursor-pointer hover:bg-gray-500 focus:outline-none hover:text-gray-50 font-titillium"
              @click="$emit('changeFont', 'titillium')"
            >
              <span class="relative col-span-1 pr-2">
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  v-show="globalStyles.font === 'font-titillium'"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span class="col-span-3">{{ $t("toolbar.fonts.d") }}</span>
            </span>
            <span
              class="grid w-full grid-cols-4 px-2 py-2 text-sm font-medium text-left text-gray-100 cursor-pointer hover:bg-gray-500 focus:outline-none hover:text-gray-50 font-poppins"
              @click="$emit('changeFont', 'poppins')"
            >
              <span class="relative col-span-1 pr-2">
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  v-show="globalStyles.font === 'font-poppins'"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span class="col-span-3">{{ $t("toolbar.fonts.e") }}</span>
            </span>
          </div>
        </transition>
      </div>
      <!-- <span class="rounded-full shadow-lg">
          <button
            class="text-center bg-gray-200 rounded-full shadow-lg focus:outline-none hover:bg-gray-100"
            @click="print()"
          >
            <svg
              class="relative flex-shrink-0 w-6 h-6 m-2 text-gray-300 group-hover:text-gray-200"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
          </button>
      </span>-->
      <span class="relative">
        <button
          class="duration-150 transform border border-transparent rounded-lg group hover:-translate-y-px hover:shadow-xs focus:outline-none"
          @click="showLayouts = !showLayouts"
          v-click-outside="closeLayoutsMenu"
        >
          <svg
            class="relative flex-shrink-0 w-6 h-6 m-2 text-gray-300 group-hover:text-gray-200"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
            />
          </svg>
        </button>
        <transition
          enter-active-class="transition duration-200 ease-out transform"
          enter-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-200 ease-in transform"
          leave-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div
            class="absolute left-0 w-48 py-1 -ml-16 text-left bg-gray-600 rounded-lg shadow-xs"
            v-show="showLayouts"
          >
            <span
              class="grid justify-end w-full grid-cols-4 px-2 py-2 text-left cursor-pointer group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="
                $emit('changeLayout', 'simple');
                showLayouts = false;
              "
            >
              <svg
                class="w-5 h-5 col-span-1 mx-2 text-gray-300 group-hover:text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                v-show="globalStyles.layout === 'simple'"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="col-span-3 col-start-2 text-sm font-semibold text-gray-100"
                >{{ $t("toolbar.layouts.a") }}</span
              >
            </span>
            <span
              class="grid justify-end w-full grid-cols-4 px-2 py-2 text-left cursor-pointer group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="
                $emit('changeLayout', 'simple-variant');
                showLayouts = false;
              "
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 col-span-1 mx-2 text-gray-300 group-hover:text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                v-show="globalStyles.layout === 'simple-variant'"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="col-span-3 col-start-2 text-sm font-semibold text-gray-100"
                >{{ $t("toolbar.layouts.b") }}</span
              >
            </span>
            <span
              class="grid justify-end w-full grid-cols-4 px-2 py-2 text-left cursor-pointer group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="
                $emit('changeLayout', 'modern');
                showLayouts = false;
              "
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 col-span-1 mx-2 text-gray-300 group-hover:text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                v-show="globalStyles.layout === 'modern'"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="col-span-3 col-start-2 text-sm font-semibold text-gray-100"
                >{{ $t("toolbar.layouts.c") }}</span
              >
            </span>
            <span
              class="grid justify-end w-full grid-cols-4 px-2 py-2 text-left cursor-pointer group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="
                $emit('changeLayout', 'modern-variant');
                showLayouts = false;
              "
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 col-span-1 mx-2 text-gray-300 group-hover:text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                v-show="globalStyles.layout === 'modern-variant'"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="col-span-3 col-start-2 text-sm font-semibold text-gray-100"
                >{{ $t("toolbar.layouts.d") }}</span
              >
            </span>
            <span
              class="grid justify-end w-full grid-cols-4 px-2 py-2 text-left cursor-pointer group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="
                $emit('changeLayout', 'elegante');
                showLayouts = false;
              "
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 col-span-1 mx-2 text-gray-300 group-hover:text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                v-show="globalStyles.layout === 'elegante'"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="col-span-3 col-start-2 text-sm font-semibold text-gray-100"
                >{{ $t("toolbar.layouts.e") }}</span
              >
            </span>
            <span
              class="grid justify-end w-full grid-cols-4 px-2 py-2 text-left cursor-pointer group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="
                $emit('changeLayout', 'elegante-variant');
                showLayouts = false;
              "
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 col-span-1 mx-2 text-gray-300 group-hover:text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                v-show="globalStyles.layout === 'elegante-variant'"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="col-span-3 col-start-2 text-sm font-semibold text-gray-100"
                >{{ $t("toolbar.layouts.f") }}</span
              >
            </span>
          </div>
        </transition>
      </span>
      <div class="relative inline-block">
        <button
          class="duration-150 transform border border-transparent rounded-lg group hover:-translate-y-px hover:shadow-xs focus:outline-none"
          @click="showFilesMenu = !showFilesMenu"
          v-click-outside="closeFilesMenu"
        >
          <svg
            class="relative flex-shrink-0 w-6 h-6 m-2 text-gray-300 group-hover:text-gray-200"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        </button>
        <transition
          enter-active-class="transition duration-200 ease-out transform"
          enter-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-200 ease-in transform"
          leave-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div
            class="absolute right-0 w-48 py-1 -ml-12 text-center bg-gray-600 rounded-lg shadow-xs md:left-0"
            v-show="showFilesMenu"
          >
            <button
              class="flex items-center w-full group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
              @click="$emit('print')"
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 mx-4 my-2 text-gray-300 group-hover:text-gray-200"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              <span class="text-sm font-medium text-gray-200">{{
                $t("toolbar.files.export")
              }}</span>
            </button>
            <slot></slot>
            <label
              class="flex items-center w-full group hover:bg-gray-500 focus:outline-none hover:text-gray-50"
            >
              <svg
                class="relative flex-shrink-0 w-5 h-5 mx-4 my-2 text-gray-300 group-hover:text-gray-200"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span class="text-sm font-medium text-gray-200">{{
                $t("toolbar.files.import")
              }}</span>
              <input
                type="file"
                id="fileinput"
                class="absolute inset-0 hidden"
                @change="$emit('selectedJSONFile')"
              />
            </label>
          </div>
        </transition>
      </div>
    </div>
    <div class="flex items-center justify-end flex-1 space-x-3 text-right">
      <div>
        <select
          @change="changeLocale"
          v-model="selectedLocale"
          id="location"
          class="form-select block sm:pr-7 px-1 py-1.5 pr-6 sm:px-3 text-sm leading-5 border-transparent focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5 bg-gray-600 text-gray-100"
        >
          <option>ES</option>
          <option selected>EN</option>
        </select>
        <!-- <button class="text-sm font-semibold text-gray-200" @click="changeLocale">EN</button> -->
      </div>
      <div>
        <button
          class="duration-150 transform border border-transparent rounded-lg group hover:-translate-y-px hover:shadow-xs focus:outline-none"
          @click="$emit('showInfo')"
        >
          <svg
            class="relative flex-shrink-0 m-2 text-gray-300 w-7 h-7 group-hover:text-gray-200"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
import Logo from "@/components/Logo";

export default {
  components: {
    Logo,
  },
  props: {
    globalStyles: {
      required: true,
    },
  },
  data() {
    return {
      selectedLocale: this.$i18n.locale.toUpperCase(),
      selectedFile: null,
      showFonts: false,
      showLayouts: false,
      showColors: false,
      showFilesMenu: false,
      showModalPrint: false,
      showModalAbout: false,
      colors: [
        { color: "blue", value: "bg-blue-500" },
        { color: "red", value: "bg-red-500" },
        { color: "green", value: "bg-green-500" },
        { color: "valencia", value: "bg-valencia-500" },
        { color: "indigo", value: "bg-indigo-500" },
        { color: "cool-gray", value: "bg-gray-500" },
        { color: "blued", value: "bg-blued-900" },
        { color: "teal", value: "bg-teal-500" },
        { color: "yellow", value: "bg-yellow-500" },
      ],
    };
  },
  methods: {
    changeLocale() {
      this.$i18n.locale = this.selectedLocale.toLowerCase();
    },
    closeColorsMenu: function () {
      // console.log("called outsideclick");
      this.showColors = false;
    },
    closeLayoutsMenu: function () {
      this.showLayouts = false;
    },
    closeFontsMenu: function () {
      this.showFonts = false;
    },
    closeFilesMenu: function () {
      this.showFilesMenu = false;
    },
  },
};
</script>

<style lang="scss" scoped>
</style>